import type { Action } from 'state/actions';
import { initialLiveQuote, initialLiveQuoteMap } from 'state/secondaryMarket/live/quote/liveQuote.model';
import { addOrReplaceKey, patchKey, removeKey } from 'state/utils/collection';

import type { LiveQuoteMap } from './liveQuote.model';
import { toSecondaryMarketPriceType } from './liveQuote.utils';

export function liveQuotesReducer(state: LiveQuoteMap = initialLiveQuoteMap, action: Action): LiveQuoteMap {
  switch (action.type) {
    case 'SECONDARY_MARKET_LIVE_QUOTE_REQUESTED':
      return addOrReplaceKey(state, action.tabId, initialLiveQuote);
    case 'SECONDARY_MARKET_LIVE_QUOTE_CANCEL_REQUESTED':
    case 'SECONDARY_MARKET_LIVE_QUOTE_CLEARED':
      return removeKey(state, action.tabId);
    case 'SECONDARY_MARKET_LIVE_QUOTE_RECEIVED':
      return patchKey(state, action.tabId, () => ({
        quoteStatus: 'QUOTED' as const,
        price: action.data.price ?? undefined,
        salesMargin: action.data.salesMargin ?? undefined,
        tradingPrice: action.data.tradingPrice ?? undefined,
        priceType: toSecondaryMarketPriceType(action.data.priceUnit ?? undefined),
        quoteId: action.data.quoteId ?? undefined,
        settlementDate: action.data.settlementDate ?? undefined,
        upfront: action.data.upfront ?? 0,
        includeUpfront: action.data.includeUpfront ?? undefined,
        quantity: action.data.quantity ?? undefined,
        accruedInterest: action.data.accruedInterest ?? undefined,
        effectiveAmount: action.data.effectiveAmount ?? undefined,
        greeks:
          action.data.greeks?.map(
            ({
              deltaInPercent,
              deltaInAmount,
              gammaInPercent,
              gammaInAmount,
              spot,
              thetaInAmount,
              thetaInPercent,
              underlyingId,
              vegaInAmount,
              vegaInPercent,
            }) => ({
              deltaInPercent: deltaInPercent ?? undefined,
              deltaInAmount: deltaInAmount ?? undefined,
              gammaInPercent: gammaInPercent ?? undefined,
              gammaInAmount: gammaInAmount ?? undefined,
              spot: spot ?? undefined,
              thetaInAmount: thetaInAmount ?? undefined,
              thetaInPercent: thetaInPercent ?? undefined,
              underlyingId: underlyingId ?? undefined,
              vegaInAmount: vegaInAmount ?? undefined,
              vegaInPercent: vegaInPercent ?? undefined,
            }),
          ) ?? [],
        tradeWay: action.data.way ?? undefined,
        tradeAuthStartTimeInMillis: action.timestamp.getTime(),
        tradeAuthDurationInMillis: action.data.quoteDurationInMilliseconds ?? undefined,
        errorMessage: undefined,
        timeout: undefined,
        cleanPrice: action.data.cleanPrice,
        quotationType: action.data.quotationType,
      }));

    case 'SECONDARY_MARKET_LIVE_QUOTE_FAILED':
      return patchKey(state, action.tabId, () => ({
        quoteStatus: 'QUOTE_FAILED' as const,
        errorMessage: action.error,
        timeout: action.timeout,
      }));
    case 'SECONDARY_MARKET_LIVE_QUOTE_EXPIRED':
      return patchKey(state, action.tabId, () => ({ quoteStatus: 'QUOTED_EXPIRED' as const }));
    case 'SECONDARY_MARKET_LIVE_QUOTE_DURATION_ENDED':
      return patchKey(state, action.tabId, () => ({
        quoteStatus: 'QUOTED_ENDED' as const,
        tradeAuthDurationInMillis: undefined,
        tradeAuthStartTimeInMillis: undefined,
      }));
    case 'SECONDARY_MARKET_LIVE_QUOTE_REJECTED':
      return patchKey(state, action.tabId, () => ({
        quoteStatus: 'QUOTE_REJECTED' as const,
        errorMessage: action.error,
      }));

    default:
      return state;
  }
}
